/* FILE: src/BottomNav.js */
import React, { useCallback } from "react";
import "./BottomNav.css";

// Tüm ikonlar merkezi dosyadan
import { HomeIcon, SearchIcon, PlusIcon, ClipsIcon } from "./icons";
// ÖZELLİK KAYDI (2026-09-19): nav ikonları artık `OzellikIkonu` üzerinden
// çiziliyor. `varyant={activeTab === X ? "selected" : "outline"}` ifadesi bu
// dosyada DÖRT ayrı satırda tekrarlanıyordu; aile adı + erişilebilir etiket +
// geçişlilik tek yerde (`runtime/ozellikKaydi.ts` `URUN_OZELLIKLERI`) kayıtlı.
// Bayrak KAPALI kolda davranış birebir aynı: `OzellikIkonu` → `IkonKoprusu`
// → `eski` (motor propları hiç okunmaz).
import { OzellikIkonu } from "./icon-system/runtime/OzellikIkonu";
import { boyutCoz } from "./icon-system/adapters/boyut";

// 📏 ÇAĞRI YERİ SAYIMI: `<BottomNav` → 1 çağrı (App.tsx:1000).
//   activeTab  = `activePage` (sayfa kimliği metni)
//   onTabChange= `handleNavChange` — gövde 5 yerde sabit metinle çağırıyor
//   profilePic = `currentUserProfile?.profilFoto` → tanımsız olabilir
type BottomNavProps = {
  activeTab?: string;
  onTabChange: (tab: string) => void;
  profilePic?: string | null;
};

/* ── KULLANIM ÖLÇÜSÜ: KATEGORİ **G — GEZİNME** (sözleşme §2.1) ───────────────
   `docs/ikon/KULLANIM_SOZLESMESI.md` §2.1 alt navigasyonu «G — gezinme»
   kategorisine koyar ve ölçüyü **26 px** olarak karara bağlar
   (`--ikon-kullanim-gezinme`, tek kaynak `src/index.css`).

   ── NE DEĞİŞTİ ──────────────────────────────────────────────────────────
   Önce: `size = 28` + `BottomNav.css`te AİLE BAŞINA `transform: scale()`
   merdiveni (ev 0,96 · artı 1,12 · 4K 0,90 · gezegen 0,94 · avatar 0,78).
   O merdivenin amacı ÇİZİMLERİN MÜREKKEP ALANINI eşitlemekti ve sözleşme
   §0.3 onu *"ajan tercihi — §2 ile ortak düzeye taşınacak"* diye işaretledi;
   §2.2 ise açıkça *"Bir ikonun dar olması kusur değildir … kutular hizalıdır,
   mürekkep değil"* diyor. Merdiven KALDIRILDI, yerine TEK kategori ölçüsü
   geldi: beş nav ikonu da aynı 26 px kutuda, ölçeksiz.

   ── NİYE SATIR-İÇİ `style` (CSS kuralı DEĞİL) ───────────────────────────
   `IconFrame` kutuyu satır-içi `style`a yazar; satır-içi bildirim stil
   sayfasını EZER (bu depoda ölçülmüş sınıf). Token bir CSS kuralından ikona
   İNEMEZ → çağrı yerinin satır-içi stiline `var()` olarak geçirilir. Bu bir
   "dağınık istisna" DEĞİL: tek sabit, tek bileşen, beş çağrı yeri de onu
   kullanıyor.

   ⚠️ İKİ KOL, İKİ BİÇİM, TEK SAYI: motor kolu token'ı (`var(...)`) okur;
   bayrak KAPALI koldaki Phosphor bileşenleri JS sayısı ister (`size={26}`),
   CSS değişkeni okuyamazlar. Bu yüzden sayı `GEZINME_PX`te bir kez yazılır
   ve token ile AYNI olmak zorundadır — ayrışırlarsa iki kol farklı boyutta
   çizer. Ayrışma riski burada yazılıdır; bayrak kalkınca `GEZINME_PX` gider. */
const GEZINME_PX = 26;
/** ⚠️ ADLANDIRMA (owner, 2026-09-14): burada "tek kaynak" YOK —
 *  **İKİ TANIM VAR, EŞİTLİK TESTİYLE KORUNUYOR.**
 *  · MOTOR kolu: kutu doğrudan CSS token'ına bağlı (`kutuOlcu`) — bu kol
 *    için sayı TS'e kopyalanmıyor, o kadarı tamamlandı.
 *  · ESKİ kol: Phosphor bileşenleri CSS değişkeni okuyamaz, JS sayısı ister
 *    (`GEZINME_PX`). Yani değer hâlâ iki yerde YAZILI.
 *  ⚠️ `GEZINME_PX` (aşağıda) hâlâ VAR çünkü bayrak KAPALI koldaki Phosphor
 *  bileşenleri CSS değişkeni okuyamaz, JS sayısı ister. İki değerin
 *  İkisinin ayrışmasını `src/__tests__/gezinmeOlcusuTekKaynak.test.ts`
 *  KİLİTLER: token ile sayı eşit değilse kapı kırmızı olur. Kilit, tek
 *  kaynağın YERİNE GEÇMEZ; bayrak kalkıp eski kol silinince `GEZINME_PX`
 *  gider ve tanım gerçekten teke iner. */
const GEZINME_TOKEN = "--ikon-kullanim-gezinme";
const GEZINME_OLCU = `var(${GEZINME_TOKEN})`;

/* ── AKTİFLİK İŞARETİ — `aria-current` TEK KAYNAK (2026-09-12, ÖLÇÜLDÜ) ─────
   Owner: *"Aktiflik NAV ÜZERİNDEN gösterilir; ikon geometrisini koruyup
   gezinme öğesinde renk dışında da görülebilen küçük bir aktiflik işareti
   kullan; mevcut nav göstergesi varsa onu bağla."*

   ── ÖLÇÜM 1: `aria-current` DEPODA HİÇ YOKTU ────────────────────────────
   `grep -rn "aria-current" src/` → **0 isabet** (tüm kaynak ağacı). Yani
   ekran okuyucu bu gezinmede "hangisi seçili" bilgisini HİÇ almıyordu;
   seçim yalnız görsel bir sinyaldi (varyant geometrisi + `#1070`'in renk
   kademesi). Eksikti → eklendi.

   ── ÖLÇÜM 2: PAYLAŞILAN BİR NAV GÖSTERGESİ YOKTU ────────────────────────
   `BottomNav.css`te aktiflik yalnız İKİ düğmede ve İKİ AYRI DİLDE vardı:
       profil  → `.bottom-nav-profile-pic.active { border-color:#262626 }` (halka)
       gezegen → `.bottom-nav-discovery-icon.active { filter: drop-shadow(…) }` (parıltı)
   Ev / keşfet / 4K düğmelerinin hiçbir göstergesi yoktu. Yani "mevcut
   göstergeyi bağla" şıkkı bu şeritte karşılıksız; ortak bir işaret eklendi.
   ⚠ AMA ÜRÜNÜN KENDİ DİLİ VAR: profil sekmeleri (`ProfileTabsMobile.css:45`)
   aktifliği ZATEN küçük bir NOKTA ile gösteriyor. Yeni bir görsel dil
   uydurmak yerine o nokta bu şeride taşındı → iki yüzey aynı işareti
   konuşur. Renk üretilmedi: `currentColor` (düğmenin kendi metin rengi).

   ── ÖLÇÜM 3: `discovery` SEKME DEĞİL, MODAL ─────────────────────────────
   `grep -rn 'setActivePage("discovery")' src/` → **0**. `handleNavChange`
   (`App.tsx:610`) `discovery`yi modal listesine alıyor ve `activePage`i
   `"home"`a çekiyor. Yani `activeTab === "discovery"` ürün akışında HİÇ
   doğmaz (aşağıdaki ifade zaten vardı — `varyant` propunda). `aria-current`
   aynı ifadeye bağlandı: bugün hiç yazılmaz, ve yazılmaması DOĞRUDUR
   (bir sayfa değil bir katman açıyor). `createMenu` de aynı sebeple dışarıda.
   ⊘ Bu bir ÜRÜN kararı değil rota gerçeğidir; rapora ayrıca yazıldı.

   ── NİYE İŞARET `aria-current`E BAĞLI (ayrı bir sınıfa değil) ───────────
   Görsel işaret ile erişilebilir durum AYRI iki kaynaktan gelseydi sessizce
   ayrışabilirlerdi (bu depoda ölçülmüş sınıf: iki liste ayrışır). CSS
   doğrudan `[aria-current="page"]`e bağlanır → nokta görünüyorsa durum da
   yazılmıştır; biri kaybolursa ikisi birden kaybolur. */
function BottomNav({ activeTab, onTabChange, profilePic }: BottomNavProps) {
  const size = GEZINME_PX;
  /** `aria-current`: seçiliyse `"page"`, değilse özniteliği HİÇ yazma.
      (Boş dize / `"false"` YAZILMAZ: ölçülmüş sınıf — `#1015`'te boş string
      axe'ı sessizce yeşil geçiriyordu.) */
  const seciliMi = (sekme: string) => (activeTab === sekme ? ("page" as const) : undefined);
  // Motor kolu: kutu TOKEN'dan gelir (`GEZINME_OLCU` → `kutuOlcu`), `boyutCoz`un
  // px yolundan DEĞİL. `boyutCoz(26)` yine çağrılıyor çünkü `data-boyut-px`
  // borç işaretini o üretiyor ve 26 hâlâ bir BOYUT TOKEN'ı değil (16/20/24/32)
  // — borcun DOM'da görünür kalması bu depodaki yerleşik kural. Ama görünen
  // kutuyu token belirler; `style` artık ölçüye HİÇ karışmaz (IconFrame'de
  // merkezî kutu son söz — 2026-09-14).
  // Bayrak kapalı → `eski` (Phosphor, `size={GEZINME_PX}`) → motor propları HİÇ
  // geçmez; iki kol aynı sayıyı görür.
  const b = boyutCoz(size);
  /* 2026-09-14 — kutu TOKEN'dan, `style` ile DEĞİL. Eskiden kutu `style`
     üzerinden geçiyordu ve `IconFrame` içindeki `...style` merkezî ölçüyü
     ezebiliyordu (owner bulgusu). Artık adlı yol: `kutuOlcu`. */
  const motorBoyut = { boyut: b.boyut, kutuOlcu: GEZINME_OLCU, "data-boyut-px": b.borc };

  const handleExploreClick = useCallback(() => {
    try {
      onTabChange("explore");
    } catch {}

    try {
      const target = "/explore";
      if (typeof window !== "undefined") {
        if (window.location.pathname !== target) {
          window.history.pushState({}, "", target);
        } else {
          // aynı path olsa bile renderPageContent path bazlıdır; popstate ile sync yapalım
          window.history.replaceState({}, "", target);
        }

        try {
          window.dispatchEvent(new PopStateEvent("popstate"));
        } catch {
          // çok eski browser fallback
          window.dispatchEvent(new Event("popstate"));
        }
      }
    } catch {}
  }, [onTabChange]);

  return (
    <nav className="bottom-nav-container">
      {/* Erişilebilir ad `aria-label`dan (2026-09-13, ÖLÇÜLDÜ): bu düğmelerin
          adı eskiden YALNIZ `title`dan türüyordu. Ad EKSİK DEĞİLDİ
          (`computeAccessibleName` altısında da dolu okudu) ama `title`,
          ad zincirinin EN ZAYIF halkasıdır ve aynı zamanda fare ipucudur;
          ikizi `ClipNavBar` zaten `aria-label` kullanıyordu — aynı görev,
          iki farklı mekanizma. `title` ipucu olarak KALIR. */}
      {/* Ana Sayfa */}
      <button
        onClick={() => onTabChange("home")}
        className="bottom-nav-btn"
        title="Ana Sayfa"
        aria-label="Ana Sayfa"
        type="button"
        aria-current={seciliMi("home")}
      >
        <OzellikIkonu ozellik="nav.home" secili={activeTab === "home"} className="nav-icon" {...motorBoyut} eski={<HomeIcon className="nav-icon" size={size} weight={activeTab === "home" ? "fill" : "regular"} />} />
      </button>

      {/* Keşfet */}
      <button
        onClick={handleExploreClick}
        className="bottom-nav-btn"
        title="Keşfet"
        aria-label="Keşfet"
        type="button"
        aria-current={seciliMi("explore")}
      >
        <OzellikIkonu ozellik="nav.explore" secili={activeTab === "explore"} className="nav-icon" {...motorBoyut} eski={<SearchIcon className="nav-icon" size={size} weight={activeTab === "explore" ? "fill" : "regular"} />} />
      </button>

      {/* Oluştur */}
      <button
        onClick={() => onTabChange("createMenu")}
        className="bottom-nav-btn"
        title="Oluştur"
        aria-label="Oluştur"
        type="button"
      >
        <OzellikIkonu ozellik="nav.create" className="nav-icon" {...motorBoyut} eski={<PlusIcon className="nav-icon" size={size} weight="regular" />} />
      </button>

      {/* MyClips — kısa video akışı (BottomNav'da "4K" pazarlama rozeti opsiyonel ileride eklenebilir) */}
      <button
        onClick={() => onTabChange("clips")}
        className="bottom-nav-btn"
        title="MyClips"
        aria-label="MyClips"
        type="button"
        aria-current={seciliMi("clips")}
      >
        {/* `navigation.clips` = 4K harfli kimlik (EMİR #46, K21 tek istisna) — motor ailesi de 4K çizer. */}
        <OzellikIkonu ozellik="nav.clips" secili={activeTab === "clips"} className="nav-icon" color={process.env.REACT_APP_FF_IKON_DIL === "outline-v1" ? "var(--ikon-outline-accent, #835bfa)" : undefined} {...motorBoyut} eski={<ClipsIcon className="nav-icon" size={size} weight={activeTab === "clips" ? "fill" : "regular"} />} />
      </button>

      {/* ─── DISCOVERY: Sosyal Keşif Butonu ─── Mevcut butonlara dokunmaz */}
      <button
        onClick={() => onTabChange("discovery")}
        className="bottom-nav-btn bottom-nav-discovery-btn"
        title="Sosyal Keşif"
        aria-label="Sosyal Keşif"
        type="button"
        aria-current={seciliMi("discovery")}
      >
        {/* Sparkle/Discovery ikonu — gradient ile özel stil */}
        <span
          className={`bottom-nav-discovery-icon ${activeTab === "discovery" ? "active" : ""}`}
          aria-hidden="true"
        >
          {/* 📏 `{...motorBoyut}` EKLENDİ (owner madde 3 — ölçülerek).
              ÖLÇÜM (gerçek alt menü, mobil 390×844): bu ÇAĞRI YERİ boyut
              propu VERMİYORDU → `IconFrame` varsayılanına düşüyordu
              (`boyut="md"` = `--ikon-boyut-md` = 1,5rem = **24 px**), oysa
              kardeş dört ikon `motorBoyut` ile **28 px** çiziliyor. Sondanın
              DOM'dan okuduğu kutular:
                 ev / büyüteç / artı / 4K → 28×28   ·   gezegen → 24×24
              ve `data-boyut-px` gezegende `null`di (borç DOM'da bile
              görünmüyordu). Owner'ın *"gezegen daha küçük algılanıyor"*
              gözleminin kaynağı bir tasarım tercihi değil, EKSİK BİR PROPTU.
              Kardeşlerle aynı kutuya alınır; çizimin kutuyu ne kadar
              doldurduğundan gelen KALAN fark `BottomNav.css`teki optik ölçek
              merdiveniyle kapatılır (gezegen 0,94).
              ⚠ Bayrak KAPALI kolda `eski="✨"` çizilir ve `IkonKoprusu` motor
              proplarını (`boyut`/`style`/`data-boyut-px`) HİÇ geçirmez →
              emoji piksel-birebir aynı kalır (delta 0, ölçüldü). */}
          <OzellikIkonu ozellik="nav.discovery" secili={activeTab === "discovery"} {...motorBoyut} eski="✨" />
        </span>
      </button>

      {/* Profil */}
      <button
        onClick={() => onTabChange("profile")}
        className="bottom-nav-btn"
        title="Profil"
        aria-label="Profil"
        type="button"
        aria-current={seciliMi("profile")}
      >
        {profilePic ? (
          <img
            src={profilePic}
            alt="Profil"
            className={`bottom-nav-profile-pic ${
              activeTab === "profile" ? "active" : ""
            }`}
          />
        ) : (
          <div
            className={`bottom-nav-profile-pic ${
              activeTab === "profile" ? "active" : ""
            }`}
            style={{ background: "#e0e0e0" }}
          />
        )}
      </button>
    </nav>
  );
}

export default BottomNav;
